import React, { useState } from 'react';
import { AlertTriangleIcon, PackageIcon, TrendingUpIcon } from 'lucide-react';
import { UrgentOrdersModal } from './UrgentOrdersModal';
import { StockListModal } from './StockListModal';
import { ExcessInventoryModal } from './ExcessInventoryModal';
import { AlertDetailModal } from './AlertDetailModal';
import { alertsData } from '../data/alertsData';
import type { Alert } from '../data/alertsData';
export function StockOverview() {
  const [isUrgentOrdersOpen, setIsUrgentOrdersOpen] = useState(false);
  const [isLowStockOpen, setIsLowStockOpen] = useState(false);
  const [isOverStockOpen, setIsOverStockOpen] = useState(false);
  const [isExcessInventoryOpen, setIsExcessInventoryOpen] = useState(false);
  const [isAlertDetailOpen, setIsAlertDetailOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const handleAlertClick = (alert: Alert) => {
    setSelectedAlert(alert);
    setIsAlertDetailOpen(true);
  };
  const lowStockItems = [{
    id: '1',
    reference: 'PVM-5-GALV-001',
    design: '5" GALV',
    desiredStock: 20,
    currentStock: 2,
    incoming: 0,
    difference: -18,
    percentage: 10,
    priority: 'critical' as const
  }, {
    id: '2',
    reference: 'LIN-STD-002',
    design: 'Linear',
    desiredStock: 15,
    currentStock: 1,
    incoming: 0,
    difference: -14,
    percentage: 7,
    priority: 'critical' as const
  }, {
    id: '3',
    reference: 'PVM-6-5/8-POLY-003',
    design: '6" 5/8 POLY',
    desiredStock: 25,
    currentStock: 5,
    incoming: 3,
    difference: -17,
    percentage: 20,
    priority: 'high' as const
  }, {
    id: '4',
    reference: 'PVM-8-5/8-GALV-004',
    design: '8" 5/8 GALV',
    desiredStock: 30,
    currentStock: 8,
    incoming: 13,
    difference: -9,
    percentage: 27,
    priority: 'medium' as const
  }];
  const overStockItems = [{
    id: '1',
    reference: 'PVM-HP-STD-001',
    design: 'High Profile',
    desiredStock: 20,
    currentStock: 22,
    incoming: 0,
    difference: 2,
    percentage: 110,
    priority: 'medium' as const
  }, {
    id: '2',
    reference: 'PVM-6-5/8-GALV-002',
    design: '6" 5/8 GALV',
    desiredStock: 100,
    currentStock: 65,
    incoming: 65,
    difference: 30,
    percentage: 130,
    priority: 'high' as const
  }];
  return <div className="space-y-6" data-id="element-531">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-532">
        <div className="bg-white rounded-lg p-5 border border-gray-200" data-id="element-533">
          <div className="flex items-center justify-between mb-3" data-id="element-534">
            <PackageIcon className="w-6 h-6 text-blue-500" data-id="element-535" />
          </div>
          <p className="text-sm text-gray-600 mb-1" data-id="element-536">Total Stock</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-537">64%</p>
        </div>
        <div onClick={() => setIsLowStockOpen(true)} className="bg-white rounded-lg p-5 border border-gray-200 cursor-pointer hover:bg-yellow-50 hover:border-yellow-300 hover:shadow-lg transition-all group" data-id="element-538">
          <div className="flex items-center justify-between mb-3" data-id="element-539">
            <PackageIcon className="w-6 h-6 text-yellow-600 group-hover:scale-110 transition-transform" data-id="element-540" />
          </div>
          <p className="text-sm text-gray-600 mb-1" data-id="element-541">Low Stock</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-542">20%</p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-543">12 references</p>
          <p className="text-xs text-yellow-700 font-semibold mt-2 group-hover:underline" data-id="element-544">
            Click to view details →
          </p>
        </div>
        <div onClick={() => setIsOverStockOpen(true)} className="bg-white rounded-lg p-5 border border-gray-200 cursor-pointer hover:bg-blue-50 hover:border-blue-300 hover:shadow-lg transition-all group" data-id="element-545">
          <div className="flex items-center justify-between mb-3" data-id="element-546">
            <PackageIcon className="w-6 h-6 text-blue-600 group-hover:scale-110 transition-transform" data-id="element-547" />
          </div>
          <p className="text-sm text-gray-600 mb-1" data-id="element-548">Over Stock</p>
          <p className="text-3xl font-bold text-gray-900" data-id="element-549">60%</p>
          <p className="text-xs text-gray-500 mt-1" data-id="element-550">8 references</p>
          <p className="text-xs text-blue-700 font-semibold mt-2 group-hover:underline" data-id="element-551">
            Click to view details →
          </p>
        </div>
        <div onClick={() => setIsUrgentOrdersOpen(true)} className="bg-red-50 rounded-lg p-5 border border-red-200 cursor-pointer hover:bg-red-100 hover:shadow-lg transition-all group" data-id="element-552">
          <div className="flex items-center justify-between mb-3" data-id="element-553">
            <AlertTriangleIcon className="w-6 h-6 text-red-600 group-hover:scale-110 transition-transform" data-id="element-554" />
          </div>
          <p className="text-sm text-red-700 mb-1" data-id="element-555">Order Required</p>
          <p className="text-3xl font-bold text-red-600" data-id="element-556">14%</p>
          <p className="text-xs text-red-600 mt-1" data-id="element-557">6 references - Critical</p>
          <p className="text-xs text-red-700 font-semibold mt-2 group-hover:underline" data-id="element-558">
            Click to view details →
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-559">
        <div className="bg-white rounded-lg p-6 border border-gray-200" data-id="element-560">
          <h3 className="text-lg font-bold text-gray-900 mb-5" data-id="element-561">
            Inventory Status
          </h3>
          <div className="space-y-4" data-id="element-562">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-lg p-4 border border-blue-200" data-id="element-563">
              <div className="flex items-center justify-between mb-3" data-id="element-564">
                <div className="flex items-center gap-3" data-id="element-565">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-blue-500/30" data-id="element-566">
                    <PackageIcon className="w-5 h-5 text-white" data-id="element-567" />
                  </div>
                  <span className="text-gray-900 font-semibold" data-id="element-568">At Hand</span>
                </div>
                <span className="text-xl font-bold text-gray-900" data-id="element-569">
                  65 machines
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2" data-id="element-570">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full shadow-sm" style={{
                width: '65%'
              }} data-id="element-571"></div>
              </div>
            </div>
            <div className="bg-gradient-to-r from-blue-50 to-blue-100/50 rounded-lg p-4 border border-blue-200" data-id="element-572">
              <div className="flex items-center justify-between mb-3" data-id="element-573">
                <div className="flex items-center gap-3" data-id="element-574">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-blue-500 rounded-lg flex items-center justify-center shadow-lg shadow-blue-400/30" data-id="element-575">
                    <TrendingUpIcon className="w-5 h-5 text-white" data-id="element-576" />
                  </div>
                  <span className="text-gray-900 font-semibold" data-id="element-577">Incoming</span>
                </div>
                <span className="text-xl font-bold text-gray-900" data-id="element-578">
                  65 machines
                </span>
              </div>
              <div className="w-full bg-blue-200 rounded-full h-2" data-id="element-579">
                <div className="bg-gradient-to-r from-blue-400 to-blue-500 h-2 rounded-full shadow-sm" style={{
                width: '65%'
              }} data-id="element-580"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg p-6 border border-gray-200" data-id="element-581">
          <h3 className="text-lg font-bold text-gray-900 mb-5" data-id="element-582">
            Critical Alerts
          </h3>
          <div className="space-y-3" data-id="element-583">
            {alertsData.map(alert => <div key={alert.id} onClick={() => handleAlertClick(alert)} className={`rounded-lg p-4 border cursor-pointer hover:shadow-md transition-all group ${alert.severity === 'critical' ? 'bg-red-50 border-red-200 hover:bg-red-100' : alert.severity === 'high' ? 'bg-orange-50 border-orange-200 hover:bg-orange-100' : 'bg-yellow-50 border-yellow-200 hover:bg-yellow-100'}`} data-id="element-584">
                <div className="flex items-start gap-3" data-id="element-585">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${alert.severity === 'critical' ? 'bg-red-600' : alert.severity === 'high' ? 'bg-orange-600' : 'bg-yellow-600'}`} data-id="element-586">
                    <AlertTriangleIcon className="w-5 h-5 text-white" data-id="element-587" />
                  </div>
                  <div className="flex-1" data-id="element-588">
                    <p className="text-gray-900 font-semibold mb-1" data-id="element-589">
                      {alert.title}
                    </p>
                    <p className="text-gray-600 text-sm" data-id="element-590">
                      {alert.count} items need attention
                    </p>
                    <p className={`text-xs font-semibold mt-2 group-hover:underline ${alert.severity === 'critical' ? 'text-red-700' : alert.severity === 'high' ? 'text-orange-700' : 'text-yellow-700'}`} data-id="element-591">
                      Click to view details →
                    </p>
                  </div>
                </div>
              </div>)}
            <div onClick={() => setIsExcessInventoryOpen(true)} className="bg-purple-50 rounded-lg p-4 border border-purple-200 cursor-pointer hover:bg-purple-100 hover:shadow-md transition-all group" data-id="element-592">
              <div className="flex items-start gap-3" data-id="element-593">
                <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" data-id="element-594">
                  <PackageIcon className="w-5 h-5 text-white" data-id="element-595" />
                </div>
                <div className="flex-1" data-id="element-596">
                  <p className="text-gray-900 font-semibold mb-1" data-id="element-597">
                    Excess inventory - Stagnant capital
                  </p>
                  <p className="text-gray-600 text-sm" data-id="element-598">
                    78 excess units • $1.18M stagnant
                  </p>
                  <p className="text-xs text-purple-700 font-semibold mt-2 group-hover:underline" data-id="element-599">
                    Click to view details →
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <UrgentOrdersModal isOpen={isUrgentOrdersOpen} onClose={() => setIsUrgentOrdersOpen(false)} data-id="element-600" />
      <StockListModal isOpen={isLowStockOpen} onClose={() => setIsLowStockOpen(false)} type="lowstock" items={lowStockItems} data-id="element-601" />
      <StockListModal isOpen={isOverStockOpen} onClose={() => setIsOverStockOpen(false)} type="overstock" items={overStockItems} data-id="element-602" />
      <ExcessInventoryModal isOpen={isExcessInventoryOpen} onClose={() => setIsExcessInventoryOpen(false)} data-id="element-603" />
      <AlertDetailModal isOpen={isAlertDetailOpen} onClose={() => setIsAlertDetailOpen(false)} alert={selectedAlert} data-id="element-604" />
    </div>;
}